// A ceiling on how many reads the Cockpit has in the air at once.
//
// WHY THIS EXISTS, measured rather than reasoned
//
// 30 August 2026, third pass. The banners were rarer and not gone. Fired at the
// live API from the browser, the failure rate is a function of concurrency and
// nothing else:
//
//   10 requests at once     0 of 10 degraded
//   30 requests at once     2 of 30 degraded    6.7%
//   60 requests at once     5 of 60 degraded    8.3%
//   480 over 18 seconds    36 failures          7.5%
//
// And during that sustained burst Postgres held **15 connections out of a limit
// of 60**, 8 of them active. So nothing was running out at the database. The
// ceiling is Cloudflare Hyperdrive's pool: past about ten requests at once it
// stops handing out connections, and a Worker that cannot get one reports a
// dropped connection. This is the "look at the Hyperdrive connection limit"
// that the 26 August note predicted and the 30 August note guessed wrong about
// twice.
//
// Retrying is treatment. This is prevention. The Cockpit's own refresh wave is
// what pushes past the ceiling: one tick of one checkbox fires five reads, four
// of the handlers ask the permission question again, and an operator with the
// Cockpit open in two tabs doubles it. That is ten to eighteen at once, which
// is exactly the zone where the table above turns from zero to seven per cent.
// So the app stops stampeding.
//
// WRITES ARE NEVER QUEUED. A write is one deliberate click, it is rare, and
// making an operator's Approve wait behind a page's worth of reads would be
// trading a banner for a worse feeling. Only reads queue, and reads are already
// deduplicated by route and params in useBackend, so this sits on top of a
// smaller number than it looks.
//
// Five, not two: below ten at once the measured failure rate is zero, two tabs
// is the realistic worst case, and five per tab leaves headroom under that
// without turning a nine-read page into nine round trips.
export const MAX_CONCURRENT_READS = 5

export type Limiter = {
  /** Resolves when a slot is free. The caller MUST call the returned release. */
  acquire: () => Promise<() => void>
  /** For tests and diagnostics. */
  inFlight: () => number
  queued: () => number
}

export function createLimiter(max: number = MAX_CONCURRENT_READS): Limiter {
  let active = 0
  const waiting: (() => void)[] = []

  const release = () => {
    active -= 1
    const next = waiting.shift()
    // A released slot goes to the next caller, not to whoever asks next, so a
    // page that mounts nine reads cannot starve the tenth indefinitely.
    if (next) next()
  }

  const acquire = async (): Promise<() => void> => {
    if (active < max) {
      active += 1
      return once(release)
    }
    await new Promise<void>((resolve) => {
      waiting.push(() => {
        active += 1
        resolve()
      })
    })
    return once(release)
  }

  return { acquire, inFlight: () => active, queued: () => waiting.length }
}

/**
 * A release that cannot be spent twice.
 *
 * Without this, a caller that released in both a catch and a finally would hand
 * out a slot it no longer holds, `active` would drift below the truth, and the
 * ceiling would quietly stop existing. That failure is silent and would look
 * exactly like this fix not working.
 */
function once(fn: () => void): () => void {
  let spent = false
  return () => {
    if (spent) return
    spent = true
    fn()
  }
}

/** The one the app uses. Module-level, so it is per tab, which is the unit that matters. */
export const readLimiter = createLimiter()
